import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ShieldCheckIcon } from "@phosphor-icons/react";

import Layout from "../../components/Layout";
import EntityHeader from "../../components/crm/EntityHeader";
import LinkedSection from "../../components/crm/LinkedSection";
import {
  ConfirmDelete,
  DateInput,
  ErrorBanner,
  Field,
  Modal,
  PrimaryButton,
  SecondaryButton,
  Select,
  TextArea,
  TextInput,
} from "../../components/crm/Form";
import { FieldCard } from "./CustomerDetail";
import { useAuth } from "../../contexts/AuthContext";
import api, { formatApiError } from "../../lib/api";

const WRITE_ROLES = ["owner", "ops"];
const MFR_OPTIONS = [
  { value: "qcells", label: "QCells" },
  { value: "silfab", label: "Silfab" },
  { value: "mission", label: "Mission" },
  { value: "rec", label: "REC" },
  { value: "others", label: "Others" },
];
const COVERAGE_TYPES = [
  { value: "labor", label: "Labor" },
  { value: "parts", label: "Parts" },
  { value: "full", label: "Full" },
];
const PLATFORM_TYPES = [
  { value: "enphase", label: "Enphase" },
  { value: "solaredge", label: "SolarEdge" },
  { value: "tesla", label: "Tesla" },
  { value: "eg4", label: "EG4" },
  { value: "generic", label: "Generic" },
];

export default function EquipmentDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const canWrite = WRITE_ROLES.includes(user?.role);

  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [delBusy, setDelBusy] = useState(false);
  const [warOpen, setWarOpen] = useState(false);

  const load = async () => {
    setErr("");
    try {
      const { data } = await api.get(`/equipment/${id}`);
      setDoc(data);
    } catch (e) {
      setErr(formatApiError(e));
    }
  };
  useEffect(() => {
    load();
  }, [id]);

  const handleDelete = async () => {
    setDelBusy(true);
    try {
      await api.delete(`/equipment/${id}`);
      nav("/equipment");
    } catch (e) {
      setErr(formatApiError(e));
      setDelBusy(false);
    }
  };

  if (!doc)
    return (
      <Layout title="Equipment">
        <ErrorBanner message={err} />
      </Layout>
    );

  return (
    <Layout title={`${doc.manufacturer} · ${doc.model}`} subtitle="Equipment record">
      <EntityHeader
        overline="equipment"
        title={`${doc.manufacturer.toUpperCase()} ${doc.model}`}
        breadcrumb={[
          { label: "Equipment", to: "/equipment" },
          { label: doc.serial_number },
        ]}
        meta={
          <>
            <span>serial · {doc.serial_number}</span>
            {doc.install_date && <span>installed · {doc.install_date}</span>}
          </>
        }
        canEdit={canWrite}
        canDelete={canWrite}
        onEdit={() => setEditOpen(true)}
        onDelete={() => setDelOpen(true)}
      />

      <ErrorBanner message={err} />

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <FieldCard label="Manufacturer" value={doc.manufacturer} mono />
        <FieldCard label="Model" value={doc.model} />
        <FieldCard label="Serial Number" value={doc.serial_number} mono />
        <FieldCard label="Install Date" value={doc.install_date || "—"} mono />
        <FieldCard
          label="Warranty Expires"
          value={doc.warranty_expires || "—"}
          mono
        />
        <FieldCard
          label="Warranty Records"
          value={String(doc.warranties.length)}
          mono
        />
        <FieldCard
          label="Notes"
          value={doc.notes || "—"}
          className="sm:col-span-2 lg:col-span-3"
        />
      </div>

      <LinkedSection
        title="Warranty Records"
        subtitle="warranty terms tied to this equipment"
        count={doc.warranties.length}
        canCreate={canWrite}
        onCreate={() => setWarOpen(true)}
        addLabel="Add Warranty"
        emptyTitle="No warranty records"
        emptyDescription="Capture each manufacturer warranty separately so reminders can be tracked."
        data-testid="equipment-warranties-section"
      >
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="font-data text-[10px] uppercase tracking-[0.18em] text-slate-600">
              <th className="text-left px-5 py-3 font-bold">Manufacturer</th>
              <th className="text-left px-5 py-3 font-bold">Platform</th>
              <th className="text-left px-5 py-3 font-bold">Coverage</th>
              <th className="text-left px-5 py-3 font-bold">Expires</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {doc.warranties.map((w) => (
              <tr
                key={w.id}
                className="cursor-pointer hover:bg-slate-50/60"
                onClick={() => nav(`/warranties/${w.id}`)}
                data-testid={`warranty-row-${w.id}`}
              >
                <td className="px-5 py-3 font-medium text-slate-900 flex items-center gap-2">
                  <ShieldCheckIcon
                    size={14}
                    weight="duotone"
                    className="text-emerald-600"
                  />
                  {w.manufacturer}
                </td>
                <td className="px-5 py-3 font-data text-xs uppercase tracking-[0.1em] text-slate-700">
                  {w.platform_type || "—"}
                </td>
                <td className="px-5 py-3 font-data text-xs uppercase tracking-[0.1em] text-slate-700">
                  {w.coverage_type}
                </td>
                <td className="px-5 py-3 font-data text-xs text-slate-700 tabular">
                  {w.expires}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </LinkedSection>

      {canWrite && (
        <>
          <EditEquipmentModal
            open={editOpen}
            onClose={() => setEditOpen(false)}
            equipment={doc}
            onSaved={(d) => {
              setEditOpen(false);
              setDoc({ ...doc, ...d });
            }}
          />
          <ConfirmDelete
            open={delOpen}
            onClose={() => setDelOpen(false)}
            onConfirm={handleDelete}
            label="Equipment"
            busy={delBusy}
          />
          <CreateWarrantyForEquipmentModal
            open={warOpen}
            onClose={() => setWarOpen(false)}
            equipmentId={doc.id}
            onCreated={() => {
              setWarOpen(false);
              load();
            }}
          />
        </>
      )}
    </Layout>
  );
}

function EditEquipmentModal({ open, onClose, equipment, onSaved }) {
  const [form, setForm] = useState({});
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        manufacturer: equipment.manufacturer,
        model: equipment.model,
        serial_number: equipment.serial_number,
        install_date: equipment.install_date || "",
        warranty_expires: equipment.warranty_expires || "",
        notes: equipment.notes || "",
      });
      setErr("");
    }
  }, [open, equipment]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.patch(`/equipment/${equipment.id}`, {
        manufacturer: form.manufacturer,
        model: form.model,
        serial_number: form.serial_number,
        install_date: form.install_date || null,
        warranty_expires: form.warranty_expires || null,
        notes: form.notes || null,
      });
      onSaved(data);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit Equipment"
      subtitle="update record"
      data-testid="equipment-edit-modal"
    >
      <form onSubmit={submit} className="space-y-4">
        <Field label="Manufacturer" required>
          <Select
            value={form.manufacturer}
            onChange={(v) => setForm({ ...form, manufacturer: v || "qcells" })}
            options={MFR_OPTIONS}
            placeholder=""
            data-testid="equipment-edit-mfr"
          />
        </Field>
        <Field label="Model" required>
          <TextInput
            required
            value={form.model}
            onChange={(v) => setForm({ ...form, model: v })}
          />
        </Field>
        <Field label="Serial Number" required>
          <TextInput
            required
            value={form.serial_number}
            onChange={(v) => setForm({ ...form, serial_number: v })}
          />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Install Date">
            <DateInput
              value={form.install_date}
              onChange={(v) => setForm({ ...form, install_date: v })}
            />
          </Field>
          <Field label="Warranty Expires">
            <DateInput
              value={form.warranty_expires}
              onChange={(v) => setForm({ ...form, warranty_expires: v })}
            />
          </Field>
        </div>
        <Field label="Notes">
          <TextArea
            value={form.notes}
            onChange={(v) => setForm({ ...form, notes: v })}
          />
        </Field>
        <ErrorBanner message={err} />
        <div className="flex justify-end gap-2 pt-2">
          <SecondaryButton type="button" onClick={onClose}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            type="submit"
            disabled={busy}
            data-testid="equipment-edit-submit"
          >
            {busy ? "Saving…" : "Save"}
          </PrimaryButton>
        </div>
      </form>
    </Modal>
  );
}

function CreateWarrantyForEquipmentModal({
  open,
  onClose,
  equipmentId,
  onCreated,
}) {
  const [form, setForm] = useState({
    manufacturer: "",
    platform_type: "",
    coverage_type: "full",
    expires: "",
    notes: "",
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        manufacturer: "",
        platform_type: "",
        coverage_type: "full",
        expires: "",
        notes: "",
      });
      setErr("");
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const payload = {
        equipment_id: equipmentId,
        manufacturer: form.manufacturer,
        coverage_type: form.coverage_type,
        expires: form.expires,
      };
      if (form.platform_type) payload.platform_type = form.platform_type;
      if (form.notes) payload.notes = form.notes;
      const { data } = await api.post("/warranties/", payload);
      onCreated(data);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add Warranty"
      subtitle="warranty record"
      data-testid="warranty-create-modal"
    >
      <form onSubmit={submit} className="space-y-4">
        <Field label="Manufacturer" required hint="Freeform.">
          <TextInput
            required
            value={form.manufacturer}
            onChange={(v) => setForm({ ...form, manufacturer: v })}
            data-testid="warranty-mfr"
          />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Platform Type">
            <Select
              value={form.platform_type}
              onChange={(v) => setForm({ ...form, platform_type: v || "" })}
              options={PLATFORM_TYPES}
              placeholder="None"
              data-testid="warranty-platform"
            />
          </Field>
          <Field label="Coverage" required>
            <Select
              value={form.coverage_type}
              onChange={(v) =>
                setForm({ ...form, coverage_type: v || "full" })
              }
              options={COVERAGE_TYPES}
              placeholder=""
              data-testid="warranty-coverage"
            />
          </Field>
        </div>
        <Field label="Expires" required>
          <DateInput
            required
            value={form.expires}
            onChange={(v) => setForm({ ...form, expires: v })}
            data-testid="warranty-expires"
          />
        </Field>
        <Field label="Notes">
          <TextArea
            value={form.notes}
            onChange={(v) => setForm({ ...form, notes: v })}
          />
        </Field>
        <ErrorBanner message={err} />
        <div className="flex justify-end gap-2 pt-2">
          <SecondaryButton type="button" onClick={onClose}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            type="submit"
            disabled={busy}
            data-testid="warranty-submit"
          >
            {busy ? "Adding…" : "Add Warranty"}
          </PrimaryButton>
        </div>
      </form>
    </Modal>
  );
}
